import React, { useState, useMemo, useEffect } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { 
  AlertTriangle, 
  AlertCircle, 
  Info, 
  Sparkles, 
  X, 
  ChevronDown, 
  ChevronRight,
  Clock,
  Calendar,
  Copy,
  FolderX,
  TrendingUp,
  AlertOctagon,
  Filter,
  FilterX,
} from 'lucide-react';
import type { AnomalyWarning } from '@/services/aiService';
import { cn } from '@/lib/utils';

interface AIInsightsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  warnings: AnomalyWarning[];
  onDismiss?: (warningId: string) => void;
  onAutoFix?: (warningId: string) => void;
}

// Category definitions with metadata (same as accordion)
const CATEGORY_CONFIG = {
  'policy-violations': {
    label: 'Policy Violations',
    icon: AlertOctagon,
    description: 'Critical compliance and policy issues',
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
  },
  'time-tracking': {
    label: 'Time Tracking Issues',
    icon: Clock,
    description: 'Missing or incomplete time entries',
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
  },
  'approval-delays': {
    label: 'Approval & Workflow',
    icon: Calendar,
    description: 'Pending approvals and workflow issues',
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
  },
  'data-quality': {
    label: 'Data Quality',
    icon: Copy,
    description: 'Duplicate entries and data inconsistencies',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
  },
  'patterns': {
    label: 'Patterns & Insights',
    icon: TrendingUp,
    description: 'Unusual patterns and optimization suggestions',
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
  },
  'project-issues': {
    label: 'Project Issues',
    icon: FolderX,
    description: 'Closed projects and project-related warnings',
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
  },
};

// Map warning types to categories (same as accordion)
const TYPE_TO_CATEGORY: Record<AnomalyWarning['type'], keyof typeof CATEGORY_CONFIG> = {
  'excessive-hours': 'policy-violations',
  'overtime-risk': 'policy-violations',
  'weekend-work': 'policy-violations',
  'missing-time': 'time-tracking',
  'duplicate-entry': 'data-quality',
  'closed-project': 'project-issues',
  'unusual-pattern': 'patterns',
  'time-and-absence-conflict': 'data-quality',
};

// Priority levels
type PriorityLevel = 'high' | 'medium' | 'low';

interface GroupedWarnings {
  category: keyof typeof CATEGORY_CONFIG;
  warnings: AnomalyWarning[];
  highestPriority: PriorityLevel;
}

export const AIInsightsSheet: React.FC<AIInsightsSheetProps> = ({
  open,
  onOpenChange,
  warnings,
  onDismiss,
  onAutoFix,
}) => {
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
  const [activeFilters, setActiveFilters] = useState<Set<PriorityLevel>>(
    new Set(['high', 'medium', 'low'])
  );
  const [selectedWarnings, setSelectedWarnings] = useState<Set<string>>(new Set());
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [areAllCollapsed, setAreAllCollapsed] = useState(false);

  // Group warnings by category and priority (same logic as accordion)
  const groupedWarnings = useMemo(() => {
    const groups = new Map<keyof typeof CATEGORY_CONFIG, AnomalyWarning[]>();

    warnings.forEach((warning) => {
      const category = TYPE_TO_CATEGORY[warning.type] || 'patterns';
      if (!groups.has(category)) {
        groups.set(category, []);
      }
      groups.get(category)!.push(warning);
    });

    // Convert to array and sort by priority
    const result: GroupedWarnings[] = Array.from(groups.entries()).map(([category, warnings]) => {
      // Sort warnings within category by severity
      const sortedWarnings = [...warnings].sort((a, b) => {
        const severityOrder = { high: 0, medium: 1, low: 2 };
        return severityOrder[a.severity] - severityOrder[b.severity];
      });

      // Determine highest priority in group
      const highestPriority = sortedWarnings[0]?.severity || 'low';

      return {
        category,
        warnings: sortedWarnings,
        highestPriority,
      };
    });

    // Sort groups by highest priority
    result.sort((a, b) => {
      const severityOrder = { high: 0, medium: 1, low: 2 };
      return severityOrder[a.highestPriority] - severityOrder[b.highestPriority];
    });

    return result;
  }, [warnings]);

  // Filter by priority
  const filteredGroups = useMemo(() => {
    return groupedWarnings
      .map((group) => ({
        ...group,
        warnings: group.warnings.filter((w) => activeFilters.has(w.severity)),
      }))
      .filter((group) => group.warnings.length > 0);
  }, [groupedWarnings, activeFilters]);

  // Calculate badge counts
  const badgeCounts = useMemo(() => {
    return {
      high: warnings.filter((w) => w.severity === 'high').length,
      medium: warnings.filter((w) => w.severity === 'medium').length,
      low: warnings.filter((w) => w.severity === 'low').length,
    };
  }, [warnings]);

  // Sync isAllSelected state when selections change
  useEffect(() => {
    const visibleWarningIds = filteredGroups.flatMap((group) =>
      group.warnings.map((w) => w.id)
    );
    const allVisible = visibleWarningIds.length > 0 && 
      visibleWarningIds.every((id) => selectedWarnings.has(id));
    setIsAllSelected(allVisible);
  }, [selectedWarnings, filteredGroups]);

  // Sync areAllCollapsed state when categories change
  useEffect(() => {
    const visibleCategories = filteredGroups.map((group) => group.category);
    const allCollapsed = visibleCategories.length > 0 &&
      visibleCategories.every((cat) => collapsedCategories.has(cat));
    setAreAllCollapsed(allCollapsed);
  }, [collapsedCategories, filteredGroups]);

  const toggleCategory = (category: string) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const toggleFilter = (priority: PriorityLevel) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(priority)) {
        next.delete(priority);
      } else {
        next.add(priority);
      }
      return next;
    });
  };

  const toggleWarningSelection = (warningId: string) => {
    setSelectedWarnings((prev) => {
      const next = new Set(prev);
      if (next.has(warningId)) {
        next.delete(warningId);
      } else {
        next.add(warningId);
      }
      return next;
    });
  };

  const handleDismissSelected = () => {
    if (onDismiss) {
      selectedWarnings.forEach((warningId) => {
        onDismiss(warningId);
      });
      setSelectedWarnings(new Set());
    }
  };

  const handleToggleSelectAll = () => {
    if (isAllSelected) {
      // Deselect all
      setSelectedWarnings(new Set());
      setIsAllSelected(false);
    } else {
      // Select all visible warnings (filtered)
      const visibleWarningIds = filteredGroups.flatMap((group) =>
        group.warnings.map((w) => w.id)
      );
      setSelectedWarnings(new Set(visibleWarningIds));
      setIsAllSelected(true);
    }
  };

  const handleToggleCollapseAll = () => {
    if (areAllCollapsed) {
      // Expand all
      setCollapsedCategories(new Set());
      setAreAllCollapsed(false);
    } else {
      // Collapse all visible categories
      const allCategories = filteredGroups.map((group) => group.category);
      setCollapsedCategories(new Set(allCategories));
      setAreAllCollapsed(true);
    }
  };

  const getSeverityIcon = (severity: AnomalyWarning['severity']) => {
    switch (severity) {
      case 'high':
        return <AlertTriangle className="h-4 w-4" />;
      case 'medium':
        return <AlertCircle className="h-4 w-4" />;
      case 'low':
        return <Info className="h-4 w-4" />;
    }
  };

  const getSeverityColor = (severity: AnomalyWarning['severity']): 'destructive' | 'warning' | 'info' => {
    switch (severity) {
      case 'high':
        return 'destructive';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
    }
  };

  const getSeverityBadgeColor = (severity: AnomalyWarning['severity']) => {
    switch (severity) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'low':
        return 'bg-blue-100 text-blue-800 border-blue-300';
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl flex flex-col h-full">
        <SheetHeader className="sticky top-0 bg-background z-10 border-b pb-4">
          {/* Row 1: Title */}
          <div className="flex items-center gap-4 flex-wrap">
            <SheetTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              AI Insights
            </SheetTitle>
          </div>

          {/* Row 2: Description (hidden on mobile) */}
          <SheetDescription className="hidden sm:block">
            Review and manage detected issues in your timesheet
          </SheetDescription>

          {/* Row 3: Control Buttons */}
          <div className="flex items-center justify-between gap-2 flex-wrap pt-3">
            {/* Left side: Action buttons */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleToggleSelectAll}
              >
                {isAllSelected ? 'Deselect All' : 'Select All'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleToggleCollapseAll}
              >
                {areAllCollapsed ? 'Expand All' : 'Collapse All'}
              </Button>
            </div>

            {/* Right side: Filter button */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm">
                  {activeFilters.size === 3 ? (
                    <Filter className="h-4 w-4" />
                  ) : (
                    <FilterX className="h-4 w-4" />
                  )}
                  <span className="ml-1.5">Filter</span>
                  {activeFilters.size < 3 && (
                    <Badge variant="secondary" className="ml-1.5 text-xs px-1.5 py-0">
                      {activeFilters.size}
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-56">
                <div className="space-y-3">
                  <h4 className="font-medium text-sm">Filter Priorities</h4>
                  <div className="space-y-2">
                    {badgeCounts.high > 0 && (
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={activeFilters.has('high')}
                          onCheckedChange={() => toggleFilter('high')}
                        />
                        <span className="text-sm flex-1">High</span>
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">
                          {badgeCounts.high}
                        </Badge>
                      </label>
                    )}
                    {badgeCounts.medium > 0 && (
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={activeFilters.has('medium')}
                          onCheckedChange={() => toggleFilter('medium')}
                        />
                        <span className="text-sm flex-1">Medium</span>
                        <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200 text-xs">
                          {badgeCounts.medium}
                        </Badge>
                      </label>
                    )}
                    {badgeCounts.low > 0 && (
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={activeFilters.has('low')}
                          onCheckedChange={() => toggleFilter('low')}
                        />
                        <span className="text-sm flex-1">Low</span>
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                          {badgeCounts.low}
                        </Badge>
                      </label>
                    )}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </SheetHeader>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Grouped warnings */}
          <div className="space-y-3">
          {filteredGroups.map((group) => {
            const config = CATEGORY_CONFIG[group.category];
            const isCollapsed = collapsedCategories.has(group.category);
            const CategoryIcon = config.icon;

            return (
              <div
                key={group.category}
                className={cn(
                  'border rounded-lg overflow-hidden transition-all',
                  config.borderColor
                )}
              >
                {/* Category header */}
                <button
                  onClick={() => toggleCategory(group.category)}
                  className={cn(
                    'w-full flex items-center justify-between p-3 text-left transition-colors hover:opacity-80',
                    config.bgColor
                  )}
                >
                  <div className="flex items-center gap-3">
                    <CategoryIcon className={cn('h-4 w-4', config.color)} />
                    <div>
                      <h4 className={cn('text-sm font-semibold', config.color)}>
                        {config.label}
                      </h4>
                      <p className="text-xs text-muted-foreground">
                        {config.description}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {group.warnings.length}
                    </Badge>
                    <Badge
                      variant="outline"
                      className={cn('text-xs', getSeverityBadgeColor(group.highestPriority))}
                    >
                      {group.highestPriority}
                    </Badge>
                    {isCollapsed ? (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </button>

                {/* Category warnings */}
                {!isCollapsed && (
                  <div className="p-2 space-y-2 bg-background">
                    {group.warnings.map((warning) => (
                      <Alert
                        key={warning.id}
                        variant={getSeverityColor(warning.severity)}
                        className="relative"
                        showIcon={false}
                      >
                        <div className="flex items-start gap-3">
                          {/* Checkbox for selection */}
                          <Checkbox
                            checked={selectedWarnings.has(warning.id)}
                            onCheckedChange={() => toggleWarningSelection(warning.id)}
                            className="mt-0.5"
                          />

                          {/* Warning content */}
                          <div className="flex-1 space-y-1 pr-8">
                            <AlertTitle className="text-sm font-medium">
                              {warning.message}
                            </AlertTitle>
                            {warning.suggestion && (
                              <AlertDescription className="text-xs">
                                💡 {warning.suggestion}
                              </AlertDescription>
                            )}

                            {/* Action buttons */}
                            {warning.autoFixAvailable && onAutoFix && (
                              <div className="mt-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs"
                                  onClick={() => onAutoFix(warning.id)}
                                >
                                  Auto-fix
                                </Button>
                              </div>
                            )}
                          </div>

                          {/* Close button (top-right) */}
                          {onDismiss && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 absolute top-2 right-2"
                              onClick={() => onDismiss(warning.id)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </Alert>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Empty state when all filtered out */}
          {filteredGroups.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Info className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No issues match the selected filters</p>
            </div>
          )}
          </div>
        </div>

        <SheetFooter className="sticky bottom-0 bg-background z-10 border-t pt-4">
          <Button
            variant="outline"
            disabled={selectedWarnings.size === 0}
            onClick={handleDismissSelected}
          >
            Dismiss Selected
            {selectedWarnings.size > 0 && ` (${selectedWarnings.size})`}
          </Button>
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
